/**
 * Video Compression Utility
 * Compresses video files client-side before upload to reduce server load
 * while maintaining quality good enough for emotional analysis
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpegInstance = null;
let isFFmpegLoaded = false;

/**
 * Load FFmpeg.wasm instance (lazy loading)
 */
async function loadFFmpeg() {
  if (isFFmpegLoaded && ffmpegInstance) {
    return ffmpegInstance;
  }

  try {
    const ffmpeg = new FFmpeg();
    
    // Load FFmpeg core
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });

    ffmpegInstance = ffmpeg;
    isFFmpegLoaded = true;
    console.log('✅ FFmpeg loaded successfully');
    return ffmpeg;
  } catch (error) {
    console.error('❌ Error loading FFmpeg:', error);
    throw new Error('Failed to load video compression engine');
  }
}

/**
 * Compress video file with optimized settings for emotional analysis
 * 
 * @param {File} file - Original video file
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<{file: File, originalSize: number, compressedSize: number, compressionRatio: number}>}
 */
export async function compressVideo(file, onProgress = () => {}) {
  const originalSize = file.size;
  const originalSizeMB = (originalSize / (1024 * 1024)).toFixed(2);
  
  console.log(`🎬 Starting compression for: ${file.name} (${originalSizeMB}MB)`);
  onProgress(0, 'Initializing compression engine...');

  try {
    // Load FFmpeg
    const ffmpeg = await loadFFmpeg();
    onProgress(10, 'Compression engine loaded');

    // Write input file to FFmpeg filesystem
    await ffmpeg.writeFile('input.mp4', await fetchFile(file));
    onProgress(20, 'Video file loaded');

    // Ultra-aggressive compression settings optimized for:
    // - AI emotional analysis (only 15 frames analyzed, basic facial expressions visible)
    // - Maximum file size reduction (10-20x reduction)
    // - Fastest encoding (H.264, preset ultrafast)
    // - 360p resolution (minimal but sufficient for AI frame analysis)
    const compressionSettings = [
      '-i', 'input.mp4',
      '-c:v', 'libx264',          // H.264 codec (widely supported, good compression)
      '-preset', 'ultrafast',     // Ultrafast encoding (prioritize speed)
      '-crf', '35',               // Quality: 35 (very compressed but AI can still detect emotions)
      '-vf', 'scale=640:-2',      // Scale to 360p width (640x360), maintain aspect ratio
      '-r', '20',                 // Reduce frame rate to 20fps (smoother than 15fps)
      '-c:a', 'aac',              // AAC audio codec
      '-b:a', '48k',              // Audio bitrate 48kbps (reduced, still intelligible)
      '-ac', '1',                 // Mono audio (sufficient for transcription)
      '-movflags', '+faststart',  // Enable fast start for web playback
      '-max_muxing_queue_size', '1024',  // Prevent muxing errors
      'output.mp4'
    ];

    // Set up progress tracking
    ffmpeg.on('progress', ({ progress }) => {
      const percent = Math.min(95, 20 + Math.round(progress * 75));
      onProgress(percent, `Compressing video... ${Math.round(progress * 100)}%`);
    });

    // Run compression
    onProgress(25, 'Compressing video...');
    await ffmpeg.exec(compressionSettings);
    
    onProgress(95, 'Finalizing...');

    // Read compressed video
    const data = await ffmpeg.readFile('output.mp4');
    const compressedBlob = new Blob([data.buffer], { type: 'video/mp4' });
    const compressedSize = compressedBlob.size;
    const compressedSizeMB = (compressedSize / (1024 * 1024)).toFixed(2);
    const compressionRatio = ((1 - compressedSize / originalSize) * 100).toFixed(1);

    // Create new File object
    const compressedFile = new File(
      [compressedBlob],
      file.name.replace(/\.[^/.]+$/, '') + '_compressed.mp4',
      { type: 'video/mp4' }
    );

    // Cleanup
    await ffmpeg.deleteFile('input.mp4');
    await ffmpeg.deleteFile('output.mp4');

    onProgress(100, 'Compression complete!');
    console.log(`✅ Compression complete: ${originalSizeMB}MB → ${compressedSizeMB}MB (${compressionRatio}% reduction)`);

    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      compressionRatio: parseFloat(compressionRatio)
    };

  } catch (error) {
    console.error('❌ Compression error:', error);
    throw new Error(`Compression failed: ${error.message}`);
  }
}

/**
 * Quick check if compression is beneficial
 * Compress ALL videos for optimal processing speed
 */
export function shouldCompress(file) {
  const sizeMB = file.size / (1024 * 1024);
  
  // Compress ALL videos for faster upload and processing
  // Only skip if already compressed by our system
  const isAlreadyCompressed = file.name.includes('compressed') || 
                              file.name.includes('_c.mp4') ||
                              file.name.includes('_emoq_optimized');
  
  if (isAlreadyCompressed) {
    return { shouldCompress: false, reason: 'Already optimized by emoQ' };
  }
  
  // Compress all videos for maximum speed
  return { shouldCompress: true, reason: `Compressing for 10x faster processing (${sizeMB.toFixed(1)}MB → ~${(sizeMB * 0.1).toFixed(1)}MB)` };
}

/**
 * Estimate compressed file size (rough estimate)
 */
export function estimateCompressedSize(originalSizeMB) {
  // Ultra-aggressive compression: 85-95% reduction
  // Conservative estimate: 85% reduction
  const estimatedSizeMB = originalSizeMB * 0.1; // 90% reduction
  return estimatedSizeMB.toFixed(1);
}
